import type * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export interface ChatSuggestionsProps extends Omit<React.ComponentProps<'div'>, 'style'> {
  /** Names the group for a screen reader, e.g. "Suggested questions". */
  label: string;
}

/**
 * Openers offered while the conversation is still empty.
 *
 * They are not messages and are not part of the conversation — picking one
 * simply writes it, as though the user had typed it themselves. They are gone
 * once there is something to read.
 */
export function ChatSuggestions({ label, className, ...props }: ChatSuggestionsProps) {
  return (
    <div
      data-slot='chat-suggestions'
      role='group'
      aria-label={label}
      className={cn('flex flex-col gap-1.5', className)}
      {...props}
    />
  );
}

export interface ChatSuggestionProps extends Omit<
  React.ComponentProps<'button'>,
  'style' | 'children'
> {
  /** Both the label and, on click, exactly what gets sent. */
  text: string;
  /**
   * Whether this is one of the answers picked so far — only meaningful for a
   * question that takes several. Left unset, this is an ordinary suggestion:
   * saying something, not holding a choice, and so not a toggle at all.
   */
  selected?: boolean;
}

/**
 * One thing the user can say by clicking rather than typing.
 *
 * A disabled one is still readable — it is part of what was said, and hiding
 * it would leave a hole in the conversation — but it is plainly no longer on
 * offer: faded, deaf to the mouse, and out of the tab order.
 *
 * Given `selected` it becomes a toggle instead: pressed or not, said in the
 * markup as well as in colour, with a tick so that the state is not carried by
 * colour alone.
 */
export function ChatSuggestion({
  text,
  selected,
  className,
  type = 'button',
  ...props
}: ChatSuggestionProps) {
  const holdsAChoice = selected !== undefined;

  return (
    <button
      data-slot='chat-suggestion'
      type={type}
      aria-pressed={selected}
      className={cn(
        'cursor-pointer rounded-sm px-3 py-2.25 text-left text-sm font-semibold transition',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
        'disabled:pointer-events-none disabled:opacity-40',
        holdsAChoice && 'flex items-center justify-between gap-2',
        selected
          ? 'bg-primary text-white hover:bg-primary-hover'
          : 'bg-fill text-ink hover:bg-fill-faint',
        className,
      )}
      {...props}
    >
      {text}
      {selected ? <Check size={15} aria-hidden='true' className='shrink-0' /> : null}
    </button>
  );
}
